/**
 * How the file browser's LIST view is ordered.
 *
 * One comparator, shared by the header buttons, the folder rows and the
 * document rows, for the same reason `docActions()` is one function: an order
 * written twice is an order that drifts, and the header that claims to sort by
 * a column is only honest if the rows below it were put in order by the same
 * rule.
 *
 * Three things in here are decisions rather than mechanics:
 *
 * 1. **Folders always sort as a GROUP ABOVE documents.** `browserOrder()`
 *    returns two arrays and never one, so there is nowhere for a document to
 *    land between two folders. This is the file-browser convention -- Finder,
 *    Explorer, Drive and every tree view ever shipped -- and it is what keeps
 *    "sort by name" useful when a folder and a document share a prefix:
 *    "Samples" the folder and "Samples 2026.md" the document are two different
 *    kinds of thing and a person looking for the folder should not have to
 *    read past the documents to find it. DO NOT "fix" this by concatenating
 *    the two lists and sorting the result.
 *
 * 2. **`Intl.Collator`, never `<`.** `<` on strings compares UTF-16 code
 *    units, which puts every capital letter before every lowercase one ("Zoo"
 *    before "apple") and every accented letter after "z" ("Ångström" after
 *    "zebra"). The collator is created ONCE at module scope: constructing one
 *    per comparison is the classic way to turn a 100-row sort into a visible
 *    pause. `sensitivity: 'base'` folds case and accents together; `numeric`
 *    makes "Draft 10" follow "Draft 9" rather than "Draft 1".
 *
 * 3. **Every comparison ends at the name, then the id.** A column with ties in
 *    it -- and `rev` is nothing but ties -- would otherwise leave those rows in
 *    whatever order the server sent, which changes under the reader when a
 *    single unrelated document is touched. The direction is applied to the
 *    WHOLE comparison, tiebreak included, so descending is the ascending order
 *    reversed exactly: one click sorts, the next undoes that click.
 *
 * Run:  node --import tsx --test packages/web/src/lib/doclist-sort.test.ts
 */

import type { DocRow, Folder } from './components/doclist-types'

/** The four sortable columns. The actions column is deliberately not one. */
export type SortKey = 'name' | 'updated' | 'rev' | 'by'
export type SortDir = 'asc' | 'desc'

export interface Sort {
  key: SortKey
  dir: SortDir
}

/** Name ascending, which is where a file browser opens. */
export const DEFAULT_SORT: Sort = { key: 'name', dir: 'asc' }

const KEYS: readonly SortKey[] = ['name', 'updated', 'rev', 'by']

/* See note 2 at the top of this file: one collator, made once. */
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

/**
 * Which way a column sorts the FIRST time it is clicked.
 *
 * A date and a revision number are asked about newest-first -- "what changed"
 * -- and a name and a person are asked about alphabetically. Clicking the
 * column again reverses whichever it is.
 */
export function defaultDir(key: SortKey): SortDir {
  return key === 'updated' || key === 'rev' ? 'desc' : 'asc'
}

/** A header click: the same column reverses, a new column starts fresh. */
export function nextSort(current: Sort, key: SortKey): Sort {
  if (current.key !== key) return { key, dir: defaultDir(key) }
  return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
}

/** `aria-sort`, and the whole accessible story of a sortable header. */
export function ariaSortOf(sort: Sort, key: SortKey): 'ascending' | 'descending' | 'none' {
  if (sort.key !== key) return 'none'
  return sort.dir === 'asc' ? 'ascending' : 'descending'
}

/* ------------------------------------------------------------ persistence */

export function formatSort(sort: Sort): string {
  return `${sort.key}:${sort.dir}`
}

/**
 * Anything that is not exactly one of the eight valid strings is the default.
 *
 * It is read out of `localStorage`, which is to say out of a store a person
 * can edit, an older build can have written and a newer one can have renamed a
 * key in -- so a bad value is an ordinary event, not an error.
 */
export function parseSort(raw: string | null | undefined): Sort {
  if (!raw) return DEFAULT_SORT
  const [key, dir] = raw.split(':')
  if (!KEYS.includes(key as SortKey)) return DEFAULT_SORT
  if (dir !== 'asc' && dir !== 'desc') return DEFAULT_SORT
  return { key: key as SortKey, dir }
}

/* ------------------------------------------------------------ comparators */

function time(iso: string | null | undefined): number {
  const t = Date.parse(iso ?? '')
  // An unparseable or absent timestamp sorts as the epoch rather than as NaN,
  // which would poison the comparison and leave the array in an arbitrary
  // order (a comparator that returns NaN is not a comparator).
  return Number.isNaN(t) ? 0 : t
}

/**
 * The timestamp the Updated column is SHOWING.
 *
 * In Trash that column shows when the document was deleted, so that is what
 * clicking it sorts by: a column that sorts by a value it is not displaying is
 * a column nobody can check.
 */
function stamp(doc: DocRow, archived: boolean): string | null | undefined {
  return archived ? (doc.archivedAt ?? doc.updated_at) : doc.updated_at
}

export interface OrderOpts {
  /** The Trash view, where Updated is Deleted. */
  archived?: boolean
}

export function compareDocs(a: DocRow, b: DocRow, sort: Sort, opts: OrderOpts = {}): number {
  const archived = opts.archived === true
  let s = 0
  switch (sort.key) {
    case 'updated':
      s = time(stamp(a, archived)) - time(stamp(b, archived))
      break
    case 'rev':
      s = a.rev - b.rev
      break
    case 'by':
      s = collator.compare(a.created_by ?? '', b.created_by ?? '')
      break
    case 'name':
      break
  }
  // See note 3: name, then id, so a tie is never left to the response order.
  if (s === 0) s = collator.compare(a.name, b.name)
  if (s === 0) s = a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  return sort.dir === 'asc' ? s : -s
}

/**
 * Folders compare by NAME whatever column is active.
 *
 * A folder has no revision and no author, and the number it shows in the
 * Updated column is a document count rather than a date -- so there is no
 * honest way to order folders by Updated, Rev or By. Reordering them by their
 * document count when a person clicked a column headed "Updated" would be a
 * different question answered silently. The direction still applies, so
 * clicking a header twice reverses the whole table and not half of it.
 */
export function compareFolders(a: Folder, b: Folder, sort: Sort): number {
  let s = collator.compare(a.name, b.name)
  if (s === 0) s = a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  return sort.dir === 'asc' ? s : -s
}

export function sortFolders(folders: readonly Folder[], sort: Sort): Folder[] {
  return [...folders].sort((a, b) => compareFolders(a, b, sort))
}

export function sortDocs(docs: readonly DocRow[], sort: Sort, opts: OrderOpts = {}): DocRow[] {
  return [...docs].sort((a, b) => compareDocs(a, b, sort, opts))
}

export interface Listing {
  folders: Folder[]
  docs: DocRow[]
}

/**
 * The list view's rows, in the order they are drawn.
 *
 * TWO arrays, on purpose. See note 1 at the top of this file: folders are a
 * group above documents, each group in the chosen order, and the only way to
 * be sure of that is for the two never to be in one array in the first place.
 */
export function browserOrder(
  folders: readonly Folder[],
  docs: readonly DocRow[],
  sort: Sort,
  opts: OrderOpts = {},
): Listing {
  return { folders: sortFolders(folders, sort), docs: sortDocs(docs, sort, opts) }
}
